import { useState, useCallback } from 'react';

export interface CepResult {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

export interface UseCepSearchReturn {
  searchCep: (cep: string) => Promise<CepResult | null>;
  isLoading: boolean;
  error: string | null;
}

/** Tempo máximo da consulta ao ViaCEP: a busca é conveniência, nunca pode travar a tela. */
export const CEP_LOOKUP_TIMEOUT_MS = 5000;

export type CepLookupOutcome =
  | { status: 'ok'; data: CepResult }
  | { status: 'notFound' }
  | { status: 'invalid' }
  | { status: 'unavailable'; message: string };

/**
 * Consulta o ViaCEP distinguindo "CEP não existe" de "serviço fora/lento" — a conferência de
 * endereço trata os dois de forma diferente (o primeiro é erro de digitação provável).
 */
export async function lookupCep(cep: string): Promise<CepLookupOutcome> {
  const cleanCep = cep.replace(/\D/g, '');
  if (cleanCep.length !== 8) return { status: 'invalid' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CEP_LOOKUP_TIMEOUT_MS);
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`, { signal: controller.signal });
    if (!response.ok) {
      return { status: 'unavailable', message: `Erro ${response.status}: Falha na consulta do CEP` };
    }
    const data = await response.json();
    // ViaCEP retorna um campo "erro" quando o CEP não existe
    if (data.erro) return { status: 'notFound' };
    return {
      status: 'ok',
      data: {
        logradouro: data.logradouro || '',
        bairro: data.bairro || '',
        localidade: data.localidade || '',
        uf: data.uf || '',
      },
    };
  } catch (err) {
    const message = err instanceof Error && err.name === 'AbortError'
      ? 'Tempo esgotado na consulta do CEP'
      : err instanceof Error ? err.message : 'Erro desconhecido ao buscar CEP';
    return { status: 'unavailable', message };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Hook para buscar informações de endereço pelo CEP
 */
export const useCepSearch = (): UseCepSearchReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchCep = useCallback(async (cep: string): Promise<CepResult | null> => {
    setError(null);
    setIsLoading(true);
    try {
      const outcome = await lookupCep(cep);
      switch (outcome.status) {
        case 'ok':
          return outcome.data;
        case 'invalid':
          setError('CEP deve ter 8 dígitos');
          return null;
        case 'notFound':
          setError('CEP não encontrado');
          return null;
        default:
          setError(outcome.message);
          console.error('Erro ao buscar CEP:', outcome.message);
          return null;
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    searchCep,
    isLoading,
    error
  };
};
